import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  dispatchChatEvent,
  parseConfirmation,
  type AgentChatError,
  type ChatEventContext,
  type ConfirmationPayload,
} from './chatEventHandlers';
import { ChatStreamCursor } from './chatStreamCursor';
import type { ChatSendBody, ChatTransport } from './ChatTransport';
import type { ChatMessage } from './types';

export type { AgentChatError } from './chatEventHandlers';

/**
 * The parsed, typed `subscribed` snapshot handed to `onSnapshot` — the
 * first frame of every stream attempt, validated once here so the mount
 * doesn't re-derive it from a raw event by casts.
 */
export interface ChatStreamSnapshot {
  /** Whether a turn is currently running for this session. */
  currentTurn: 'busy' | null;
  /**
   * The still-pending tool-use confirmation, or null. Authoritative in
   * both directions: null means any confirmation the client still shows
   * was resolved while it was disconnected (another tab, or a
   * server-side timeout) and must be dismissed.
   */
  pendingConfirmation: ConfirmationPayload | null;
}

/**
 * Where the session is in a turn. One value rather than two booleans,
 * because they were never independent — cancelling implies a live turn,
 * and 'stopping' is that dependency spelled out. Returning to 'idle'
 * clears a pending cancel by construction, so no transition has to
 * remember to lower a second flag alongside the first.
 */
type TurnPhase = 'idle' | 'active' | 'stopping';

interface UseChatMessagesOptions {
  transport: ChatTransport;
  currentSessionID: string | null;
  /**
   * The routine open in the Routine editor (URL-derived only, no
   * fallback). Forwarded into `ChatSendBody.editingRoutineID` so the
   * agent can resolve "this routine" against the page the operator
   * is on, not against whatever happens to be loaded.
   */
  editingRoutineID: string | null;
  /**
   * The routine loaded on the robot (`robot.latestRoutineID`).
   * Forwarded into `ChatSendBody.loadedRoutineID` so the agent can
   * resolve "the running routine" against the actual loaded id,
   * even when the user is editing a different one.
   */
  loadedRoutineID: string | null;
  /**
   * Optional dispatch hook for event shapes the standard handler
   * doesn't recognize (anything other than the streaming-turn events
   * handled by `chatEventHandlers` and the `subscribed` snapshot,
   * which has its own typed callback below). Called with the raw
   * event and an `append` helper that pushes a message into the same
   * state the hook owns. Used by the dev harness (ui-mock) to layer
   * mock-only event kinds without subscribing to `/events` twice.
   * Production builds use it to dispatch `confirmation_request` into
   * the panel provider and `navigate`/`card` frames into the router.
   */
  onUnknownEvent?: (
    event: unknown,
    append: (message: ChatMessage) => void,
  ) => void;
  /**
   * Called with the parsed (re)connect snapshot — always the first
   * frame of every stream attempt. The mount restores (or dismisses)
   * the tool-use approval modal from `pendingConfirmation`; the hook
   * itself has already synced the thinking affordance and its seq
   * tracking before this fires.
   */
  onSnapshot?: (snapshot: ChatStreamSnapshot) => void;
  /**
   * [TFIX] Called when the BE allocates a sessionID for a
   * previously session-less /send (i.e. the first message in a
   * brand-new chat). The mount uses this to pin `currentSessionID`
   * in the panel provider so the next /send and the /events
   * subscription target the same session.
   */
  onSessionCreated?: (sessionID: string) => void;
  /**
   * Called when the hook detects that events were lost for good — a
   * seq gap in the stream, or a `subscribed` snapshot showing the
   * turn ended while we were disconnected (its frames were flushed
   * server-side and can't replay). Fired only once the turn is over,
   * so the mount can rebuild `messages` from persisted history (the
   * feathers seed) without clobbering a live stream.
   */
  onResyncNeeded?: () => void;
}

interface UseChatMessagesResult {
  messages: ChatMessage[];
  isSending: boolean;
  streamError: Error | null;
  /**
   * Terminal agent error from the orchestrator, or null. Surfaced as a
   * banner; cleared on a new send, a session switch, or "New chat".
   */
  agentError: AgentChatError | null;
  /**
   * True while a turn is in flight but nothing else on screen is moving —
   * from send until the first streamed token or tool card, and again
   * between a tool result and the next LLM response. Those are the
   * longest silent stretches of a turn (the model is ingesting the
   * prompt or generating a large tool payload); the mount renders a
   * ThinkingIndicator so the agent doesn't look dead during them.
   * Inferred client-side from the event stream, so it can only say
   * "working", not what the work is.
   */
  isThinking: boolean;
  /**
   * True while a turn is in flight on this session — from send (or the
   * first turn-progress event, for peer tabs) until the terminal
   * `done`/`error` event. Unlike `isThinking`, it stays raised during
   * visible streaming. Drives the composer's stop affordance.
   */
  isTurnActive: boolean;
  /**
   * True while a `stopTurn` cancel POST is in flight, until the turn
   * settles (or the POST fails and stop becomes retryable). Disables
   * the stop button so it can't double-fire.
   */
  isCancelling: boolean;
  sendMessage: (text: string) => Promise<void>;
  /**
   * Cancel the in-flight turn via `POST /cancel`. The orchestrator
   * aborts and emits a terminal `done` (reason `user_canceled`) on the
   * stream, which settles the UI; a 409 (turn already over) settles
   * locally. No-op when no turn is active, no session is bound yet
   * (the sub-second window before the first /send's 202 pins the
   * BE-issued sessionID), or a cancel is already in flight.
   */
  stopTurn: () => Promise<void>;
  /**
   * Re-post a previously-failed user message (marked
   * `sendStatus: 'failed'`). The matching bubble's status flips back
   * to undefined while the retry is in flight; on a fresh failure it
   * returns to 'failed' so the inline "Not sent. Tap to retry."
   * affordance reappears.
   */
  retryMessage: (messageID: string) => Promise<void>;
  /**
   * Drop messages and clear the stream-error banner. Call this when
   * the mount wants a clean slate (e.g. "New chat" from the kebab
   * menu) without waiting for a `currentSessionID` transition.
   */
  reset: () => void;
  /**
   * Turn-safe transcript replacement for the mount's async seed
   * loader. Applies the seeded rows only when no turn is live —
   * otherwise refuses (returning false) and re-arms the resync flag so
   * the next turn end reseeds with fresher rows. Prevents a stale
   * in-flight feathers fetch from wiping an optimistic user bubble or
   * streamed content that arrived while it was pending.
   */
  applySeed: (seeded: ChatMessage[]) => boolean;
}

/** Carries the HTTP status from a non-2xx `/send` to the catch block. */
class SendFailedError extends Error {
  public constructor(public readonly status: number) {
    super(`POST /send returned HTTP ${status}`);
    this.name = 'SendFailedError';
  }
}

/**
 * Copy for a `/send` that never reached the orchestrator. This is the only
 * error surface available on the first send of a new chat: the `/events`
 * subscription — and with it the stream-disconnect status and the agent
 * error banner — opens only once the backend has issued a sessionID, which
 * a failed send never returns. Without this the operator sees an 11px
 * "Not sent" hint and nothing else, which reads as the agent hanging.
 */
function sendFailureError(status: number | null): AgentChatError {
  if (status === 502 || status === 503 || status === 504) {
    return {
      code: 'agent_unreachable',
      message:
        'Can’t reach the agent service on this robot. It may still be starting up — try again shortly.',
    };
  }

  if (status === 401 || status === 403) {
    return {
      code: 'agent_unauthorized',
      message:
        'This session is no longer authorized to use the agent. Reload the page and sign in again.',
    };
  }

  return {
    code: 'send_failed',
    message: 'The message could not be sent. Tap the message to retry.',
  };
}

/**
 * Owns the events subscription, message state, and send pipeline for
 * the Routine Agent Chat panel. Extracted from `RoutineAgentChatMount`
 * so the mount itself can focus on layout + slot wiring.
 *
 * Responsibilities:
 *
 * 1. Subscribe to `transport.events(...)` while the panel is open and
 *    a session id is bound. Reconnects, disconnects, and duplicate
 *    suppression all live here: the server's replay is cursor-free, so
 *    a reconnect re-delivers the in-flight ring and `ChatStreamCursor`
 *    drops frames whose seq was already applied.
 * 2. Reset local state when the session id transitions away from a
 *    previously-bound session (new session = clean slate; close +
 *    reopen on the same session keeps the history).
 * 3. POST `transport.send(...)` and route per-message failures back
 *    into the optimistic bubble's `sendStatus`.
 *
 * Event-shape handling lives in `chatEventHandlers`; seq/epoch/resync
 * tracking lives in `ChatStreamCursor`.
 */
export function useChatMessages({
  transport,
  currentSessionID,
  editingRoutineID,
  loadedRoutineID,
  onUnknownEvent,
  onSnapshot,
  onSessionCreated,
  onResyncNeeded,
}: UseChatMessagesOptions): UseChatMessagesResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [streamError, setStreamError] = useState<Error | null>(null);
  const [agentError, setAgentError] = useState<AgentChatError | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  // The turn lifecycle as one value. 'stopping' implies a live turn, so
  // the two booleans consumers see are derived from it below — an
  // invariant by construction rather than one two flags have to keep.
  const [turnPhase, setTurnPhase] = useState<TurnPhase>('idle');
  // Tracks the most recent session whose state we reset. Used to
  // distinguish "session actually changed" (clean slate) from
  // "panel closed and reopened on the same session" (keep history).
  const lastResetSessionRef = useRef<string | null>(null);
  // Used by sendMessage's finally to skip setIsSending(false) after
  // the component unmounts (panel closed mid-flight, etc.).
  const isMountedRef = useRef(true);
  // Pin the transport for the lifetime of the consumer so swapping
  // the prop (which shouldn't happen in practice) doesn't tear down
  // the open /events subscription mid-conversation.
  const transportRef = useRef(transport);
  // Same idea for the extension callbacks: updates land in refs so
  // the /events subscription doesn't tear down on every render.
  const onUnknownEventRef = useRef(onUnknownEvent);
  const onSnapshotRef = useRef(onSnapshot);
  // [TFIX] Ref-pin the auto-create callback for the same reason —
  // sendMessage's identity already depends on `currentSessionID`,
  // we don't want it to also churn whenever the caller passes a
  // fresh `setCurrentSessionID` reference each render.
  const onSessionCreatedRef = useRef(onSessionCreated);
  const onResyncNeededRef = useRef(onResyncNeeded);
  // Mirror the messages array in a ref so `retryMessage` can look up
  // the failed message's text without re-creating itself on every
  // message change (which would churn the MessageList's `onRetry`
  // prop identity).
  const messagesRef = useRef<ChatMessage[]>(messages);
  // [TFIX] Tracks the synthetic id of the assistant message currently
  // accumulating text_delta chunks for the in-flight turn. Cleared
  // when assistant_message_persisted swaps in the BE-issued id, or
  // when `done` signals the turn is over.
  const streamingMessageIdRef = useRef<string | null>(null);
  // [TFIX] Tracks the synthetic id of the ServerToolGroup message
  // currently collecting tool_use rows. Cleared whenever a text_delta,
  // assistant_message_persisted, or done event signals the tool batch
  // is over — so the next tool_use opens a fresh group.
  const toolGroupMessageIdRef = useRef<string | null>(null);
  // [TFIX] Synthetic id of the optimistic user bubble inserted by the
  // last `sendMessage` call. The matching `user_message_persisted`
  // event swaps this id for the BE-issued messageID so the sender's
  // bubble lines up with what peer clients render (and with what the
  // seed loader reads back from feathers).
  const pendingUserMessageIdRef = useRef<string | null>(null);
  // Seq/epoch/resync tracking for the stream — duplicate suppression
  // (the server's replay is cursor-free), gap detection, and the
  // consumed-at-turn-end resync flag, as one unit-testable object.
  const cursorRef = useRef(new ChatStreamCursor());
  // Mirrors of isSending/isThinking for `applySeed`, which must read
  // the current turn state from a stable callback without churning its
  // identity (same pattern as messagesRef above). The turn-phase mirror
  // serves `stopTurn` the same way.
  const isSendingRef = useRef(false);
  const isThinkingRef = useRef(false);
  const turnPhaseRef = useRef<TurnPhase>('idle');
  // Monotonic id for cancel POSTs, so a response resolving after the
  // turn it targeted has settled (or after a newer cancel superseded
  // it) can be recognized as stale and dropped.
  const cancelSeqRef = useRef(0);
  // Monotonic epoch for session-scoped state, bumped by every
  // `resetSessionState`. A send POST carries no AbortSignal, so one
  // issued against a transcript the operator has since left ("New chat",
  // a Chat History pick) still settles here — and comparing session ids
  // wouldn't catch it, because "New chat" leaves the id null on both
  // sides. The epoch tells those apart.
  const sessionEpochRef = useRef(0);

  transportRef.current = transport;
  onUnknownEventRef.current = onUnknownEvent;
  onSnapshotRef.current = onSnapshot;
  onSessionCreatedRef.current = onSessionCreated;
  onResyncNeededRef.current = onResyncNeeded;
  messagesRef.current = messages;
  isSendingRef.current = isSending;
  isThinkingRef.current = isThinking;

  // The one writer of the phase. Ref-mirrored immediately so same-tick
  // decisions (stopTurn's guard, applySeed) see the transition before
  // React flushes.
  const setPhase = useCallback((phase: TurnPhase) => {
    turnPhaseRef.current = phase;
    setTurnPhase(phase);
  }, []);

  // Turn-active as the event dispatch and the send pipeline see it.
  // Going idle drops a pending cancel along with the turn it targeted
  // (its terminal event, snapshot, or reset arrived), so the stop button
  // never sticks disabled after the turn is gone. Raising is not a
  // downgrade: turn-progress frames keep arriving while a cancel POST is
  // in flight, and the button stays disabled until the turn settles.
  const setTurnActive = useCallback(
    (value: boolean) => {
      if (!value) {
        setPhase('idle');

        return;
      }

      if (turnPhaseRef.current === 'idle') setPhase('active');
    },
    [setPhase],
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // The one place session-scoped state is wiped: refs, seq tracking,
  // and the visible message/error/thinking state. Callers own
  // `lastResetSessionRef` themselves — its next value differs per
  // call site.
  const disownInFlightSend = useCallback(() => {
    sessionEpochRef.current += 1;
    // The epoch bump already disowned any in-flight send, so its
    // `finally` no longer speaks for this transcript either. Leaving the
    // flag set would keep the composer disabled and make `applySeed` read
    // the wait as a live turn and refuse the new session's history, both
    // for as long as the abandoned POST takes to settle.
    setIsSending(false);
  }, []);

  const resetSessionState = useCallback(() => {
    disownInFlightSend();
    streamingMessageIdRef.current = null;
    toolGroupMessageIdRef.current = null;
    pendingUserMessageIdRef.current = null;
    cursorRef.current.reset();
    setMessages([]);
    setStreamError(null);
    setAgentError(null);
    setIsThinking(false);
    setTurnActive(false);
  }, [disownInFlightSend, setTurnActive]);

  // Clear local state when the consumer drops `currentSessionID` to
  // null — typically the kebab menu's "New chat" action.
  useEffect(() => {
    if (currentSessionID !== null) return;

    lastResetSessionRef.current = null;
    resetSessionState();
  }, [currentSessionID, resetSessionState]);

  // Everything the per-event handlers may touch. Hoisted to hook scope
  // (over stable setters + refs, so its identity never churns) because
  // the stop pipeline settles a turn through the same handler path the
  // stream does — see `settleTurn` in `stopTurn`'s 409 branch.
  // `consumeResync…` closes over the cursor + mount callback so the
  // terminal handlers can trigger the reseed without knowing about
  // either.
  const eventContext = useMemo<ChatEventContext>(
    () => ({
      setMessages,
      setIsThinking,
      setIsTurnActive: setTurnActive,
      setAgentError,
      streamingMessageIdRef,
      toolGroupMessageIdRef,
      pendingUserMessageIdRef,
      consumeResyncAtTurnEnd: () => {
        if (cursorRef.current.consumeAtTurnEnd()) {
          onResyncNeededRef.current?.();
        }
      },
    }),
    [setTurnActive],
  );

  // Subscribe to /events whenever a session is bound — open OR
  // closed. The panel hiding is presentation only: the agent's turn
  // keeps running server-side, so tearing the stream down on close
  // (the old behavior) both lost live progress and forced a
  // resubscribe-with-replay on reopen that duplicated content.
  // Keeping the subscription means minimize/reopen is a pure render
  // toggle over continuously-current state.
  useEffect(() => {
    if (currentSessionID === null) return undefined;

    const controller = new AbortController();

    // [TFIX] Genuine session SWITCH = clean slate, but only when
    // moving between two non-null session ids. The first-bind
    // (null → realID) case is NOT a switch: it's the BE allocating
    // an id for the just-sent message, and wiping at that point
    // would drop the user message we optimistically appended in
    // sendMessage and flash the panel back to the landing hero.
    // Likewise, a simple close → reopen on the same session must
    // keep the message history.
    if (lastResetSessionRef.current !== currentSessionID) {
      if (lastResetSessionRef.current !== null) {
        resetSessionState();
      } else {
        // First bind is not a wipe — the optimistically appended user
        // message has to survive — but it is still a move to a different
        // transcript, so a send issued from the landing view stops
        // speaking for what is on screen. Disowning without resetting
        // covers the Chat History pick made from a brand-new chat:
        // `resetSessionState` never runs on that path, so without this
        // the stale-send guard stays inert and a failure from the
        // abandoned send banners the session just opened.
        disownInFlightSend();
      }
    }

    lastResetSessionRef.current = currentSessionID;

    transportRef.current.events({
      sessionID: currentSessionID,
      onError: (error) => {
        if (controller.signal.aborted) return;

        setStreamError(error);

        // A disconnected stream can't vouch for turn progress — pulsing
        // dots under the "Stream disconnected" banner would assert
        // liveness the client can't see. Lowering is safe: the NDJSON
        // client reconnects with backoff and the events endpoint replays
        // the in-flight ring, which re-raises or settles the indicator
        // as the state machine dictates.
        setIsThinking(false);
      },
      onEvent: (event) => {
        // Drop late events that arrive after the consumer aborted —
        // typically a session transition (currentSessionID changed)
        // or panel close. Without this guard, a buffered event could
        // clear the new subscription's error banner or inject a
        // message from the previous session.
        if (controller.signal.aborted) return;

        // Any event arriving on the stream proves the connection is
        // healthy again — clear the stale disconnect banner so it
        // doesn't sit alongside fresh messages after an
        // auto-reconnect. (Even a duplicate frame is proof of life,
        // so this precedes the dedup drop below.)
        setStreamError(null);

        if (typeof event !== 'object' || event === null) {
          onUnknownEventRef.current?.(event, (message) => {
            setMessages((prev) => [...prev, message]);
          });

          return;
        }

        const record = event as Record<string, unknown>;

        // Duplicate suppression + gap detection. The server's replay is
        // cursor-free, so a reconnect re-delivers frames we already
        // applied — drop them here by seq. A seq jump flags a resync,
        // consumed at the turn's end.
        if (!cursorRef.current.observe(record.seq)) return;

        // The (re)connect snapshot — always the first frame of every
        // stream attempt. Sync seq/epoch tracking and the thinking
        // affordance to the server's authoritative turn state; a turn
        // that ended while we were disconnected reseeds from persisted
        // history (the only complete source at that point).
        if (record.type === 'subscribed') {
          const { isBusy } = cursorRef.current.reconcileSnapshot(record);

          // The snapshot is authoritative in both directions: a busy
          // turn raises the stop affordance on (re)connect, a settled
          // one clears any turn state (and pending cancel) left over
          // from before a disconnect.
          setTurnActive(isBusy);

          if (isBusy) {
            setIsThinking(true);
          } else {
            setIsThinking(false);

            if (cursorRef.current.consumeAtTurnEnd()) {
              streamingMessageIdRef.current = null;
              toolGroupMessageIdRef.current = null;
              onResyncNeededRef.current?.();
            }
          }

          // The snapshot carries `pendingConfirmation`, which the mount
          // restores into the approval modal (a reload during a pending
          // confirmation would otherwise lose it and leave the turn
          // blocked until the gate times out).
          onSnapshotRef.current?.({
            currentTurn: isBusy ? 'busy' : null,
            pendingConfirmation: parseConfirmation(record.pendingConfirmation),
          });

          return;
        }

        if (dispatchChatEvent(record, eventContext)) return;

        onUnknownEventRef.current?.(event, (message) => {
          setMessages((prev) => [...prev, message]);
        });
      },
      signal: controller.signal,
    });

    return () => {
      controller.abort();

      // With the subscription gone (session switched or unmount),
      // turn progress is unobservable — a turn that finishes while
      // unsubscribed would otherwise leave stale dots, since its
      // `done`/`error` frames were never seen. Lowering is safe for
      // turns still in flight: resubscribing drains the replay buffer,
      // which re-raises the indicator as the state machine dictates —
      // the same recovery path as a stream disconnect. Turn-active
      // follows the same rule: the reconnect snapshot re-raises it when
      // the turn is still running.
      setIsThinking(false);
      setTurnActive(false);
    };
  }, [
    currentSessionID,
    disownInFlightSend,
    eventContext,
    resetSessionState,
    setTurnActive,
  ]);

  // Post a `/send` for the given user-message id + text. On failure
  // flips that message's `sendStatus` to 'failed' so `UserMessage`
  // renders the red retry affordance; on success leaves it absent.
  //
  // [TFIX] When the send was issued with sessionID === null (first
  // message of a brand-new chat), parses the response body and pings
  // `onSessionCreated` with the BE-issued sessionID so the provider
  // can pin it for follow-up sends + the /events subscription.
  const postSend = useCallback(
    async (messageID: string, text: string) => {
      // Which transcript this send belongs to. Checked before the catch
      // touches any visible state, so a failure that arrives after the
      // operator moved on lands on the floor instead of on the session
      // they are reading now.
      const sendEpoch = sessionEpochRef.current;

      setIsSending(true);
      // Clear any prior terminal-error banner on a fresh send/retry. A
      // still-exhausted token budget will re-emit the error event and
      // re-raise the banner; a transient failure (e.g. llm_unavailable)
      // clears so the operator sees the retry proceed.
      setAgentError(null);
      // The turn is in flight from this moment until the first streamed
      // token / tool card; show the thinking affordance for the gap —
      // and the stop affordance for the whole turn.
      setIsThinking(true);
      setTurnActive(true);

      const body: ChatSendBody = {
        sessionID: currentSessionID,
        editingRoutineID,
        loadedRoutineID,
        text,
      };

      try {
        const response = await transportRef.current.send(body);

        if (!response.ok) {
          throw new SendFailedError(response.status);
        }

        // Same staleness test the catch applies: `onSessionCreated` is
        // the panel's `setCurrentSessionID`, so binding a session the
        // operator has already left pins them back to the transcript
        // they abandoned and replays it over the one they are reading.
        if (
          currentSessionID === null &&
          sessionEpochRef.current === sendEpoch
        ) {
          try {
            const data = (await response.clone().json()) as {
              sessionID?: unknown;
            };

            if (typeof data.sessionID === 'string' && data.sessionID) {
              onSessionCreatedRef.current?.(data.sessionID);
            }
          } catch {
            // Body wasn't JSON / didn't carry sessionID. Older BE
            // builds returned {turnID, startedAt} only; silently
            // skip so we don't surface a parse failure as a
            // send error.
          }
        }
      } catch (err) {
        if (isMountedRef.current && sessionEpochRef.current === sendEpoch) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === messageID ? { ...m, sendStatus: 'failed' } : m,
            ),
          );

          // A network-level throw (fetch rejected, no response) lands here
          // with no status and gets the generic copy.
          setAgentError(
            sendFailureError(
              err instanceof SendFailedError ? err.status : null,
            ),
          );

          // The send never started a turn — nothing is thinking and
          // there is nothing to stop. The failed bubble's retry
          // affordance is the active UI now.
          setIsThinking(false);
          setTurnActive(false);
        }
      } finally {
        if (isMountedRef.current) setIsSending(false);
      }
    },
    [currentSessionID, editingRoutineID, loadedRoutineID, setTurnActive],
  );

  // POST /cancel for the in-flight turn. The happy path deliberately
  // changes nothing locally: the orchestrator's abort emits a terminal
  // `done` (reason `user_canceled`) on the stream, and that event —
  // not this response — settles the transcript, the thinking dots, and
  // the stop affordance, exactly as it does for peer tabs.
  const stopTurn = useCallback(async () => {
    if (currentSessionID === null) return;
    if (turnPhaseRef.current !== 'active') return;

    setPhase('stopping');

    cancelSeqRef.current += 1;
    const cancelSeq = cancelSeqRef.current;

    // True while the response still speaks for the pending cancel: the
    // turn it targeted settling by any other means (terminal event,
    // snapshot, session reset) lowers isCancelling via setTurnActive,
    // and a newer cancel bumps the seq. A stale response must not
    // touch shared turn state — it could clobber a later turn's.
    const isCurrent = () =>
      cancelSeq === cancelSeqRef.current && turnPhaseRef.current === 'stopping';

    try {
      const response = await transportRef.current.cancel({
        sessionID: currentSessionID,
      });

      if (!isMountedRef.current || !isCurrent()) return;

      if (response.status === 409) {
        // The turn ended on its own before the cancel landed, and its
        // terminal event was missed (had one been applied, isCurrent
        // would have failed). Run the terminal path locally instead of
        // waiting for an event that isn't coming — dispatched rather
        // than re-implemented, so settlement keeps exactly one
        // definition: a dangling streaming bubble can't absorb the next
        // turn's text, tool rows whose result never arrived stop
        // shimmering, and a flagged gap still resyncs.
        dispatchChatEvent({ type: 'done' }, eventContext);

        return;
      }

      if (!response.ok) {
        // Unexpected status — the turn is still running server-side.
        // Re-arm the stop button so the operator can retry.
        setPhase('active');
      }
    } catch {
      if (isMountedRef.current && isCurrent()) setPhase('active');
    }
  }, [currentSessionID, eventContext, setPhase]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();

      if (!trimmed || isSending) return;

      const userMessageID = `user-${Date.now()}`;

      // [TFIX] Pin the optimistic id so the matching
      // `user_message_persisted` event can swap it for the BE-issued
      // messageID (instead of appending a duplicate bubble).
      pendingUserMessageIdRef.current = userMessageID;

      setMessages((prev) => [
        ...prev,
        { id: userMessageID, role: 'user', text: trimmed },
      ]);

      await postSend(userMessageID, trimmed);
    },
    [postSend, isSending],
  );

  const retryMessage = useCallback(
    async (messageID: string) => {
      if (isSending) return;

      const target = messagesRef.current.find((m) => m.id === messageID);

      if (
        target === undefined ||
        target.role !== 'user' ||
        target.sendStatus !== 'failed'
      ) {
        return;
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageID ? { ...m, sendStatus: undefined } : m,
        ),
      );

      await postSend(messageID, target.text);
    },
    [postSend, isSending],
  );

  // Replace the transcript with seed rows loaded from persisted
  // history — but ONLY while no turn is live. The seed fetch is async;
  // a turn that starts in its flight window (optimistic user bubble,
  // streamed deltas) would be silently wiped by a blind overwrite
  // once the stale rows resolve. Refusing the stale seed and re-arming
  // the resync flag instead means the NEXT turn end re-runs the seed
  // with rows that include everything the refused snapshot missed.
  const applySeed = useCallback((seeded: ChatMessage[]): boolean => {
    const turnLive =
      isSendingRef.current ||
      isThinkingRef.current ||
      turnPhaseRef.current !== 'idle' ||
      streamingMessageIdRef.current !== null ||
      pendingUserMessageIdRef.current !== null;

    if (turnLive) {
      cursorRef.current.flagResync();

      return false;
    }

    setMessages(seeded);

    return true;
  }, []);

  const reset = useCallback(() => {
    lastResetSessionRef.current = null;
    resetSessionState();
  }, [resetSessionState]);

  return {
    messages,
    isSending,
    streamError,
    agentError,
    isThinking,
    isTurnActive: turnPhase !== 'idle',
    isCancelling: turnPhase === 'stopping',
    sendMessage,
    stopTurn,
    retryMessage,
    reset,
    applySeed,
  };
}
